// Pure decision logic for the announcement screens (plan PR-2 §9.1).
//
// admin-web has no jsdom or @testing-library, so "does the publish button
// render?" is not a testable question. Everything that decides WHAT renders
// therefore lives here as pure functions the node-side suite can cover, and the
// components only translate the answers into JSX.

import type { AnnouncementStatus, ServiceRole } from '../../api.js';
import { ROLE_PERMISSIONS } from '../../../../shared-types/src/index.js';

export type AnnouncementAction = 'edit' | 'publish' | 'archive';

/** Which row actions a given status/permission pair may offer.
 *
 *  Read-only roles get an EMPTY list rather than disabled buttons: a button that
 *  only ever answers 403 is worse than no button (plan §3 "선제 숨김"). */
export function visibleAnnouncementActions(
  status: AnnouncementStatus,
  canWrite: boolean,
): AnnouncementAction[] {
  if (!canWrite) return [];
  if (status === 'draft') return ['edit', 'publish', 'archive'];
  // Published rows stay editable (a typo fix must not require a new notice) but
  // cannot be published twice — the server answers 409.
  if (status === 'published') return ['edit', 'archive'];
  return [];
}

/**
 * The roles that decide TENANT permissions for the current session.
 *
 * A platform/partner admin's own role carries no `admin.*` permission — it
 * obtains tenant powers by acting INTO a tenant, which yields a separate role
 * set. Treating the base role as tenant write permission would show write
 * buttons that the tenant surface then rejects.
 */
export function effectiveTenantRoles(input: {
  baseRoles: readonly ServiceRole[];
  actAsRoles?: readonly ServiceRole[];
}): ServiceRole[] {
  const base = [...input.baseRoles];
  const isControlPlane =
    base.includes('platform_admin') || base.includes('partner_admin');
  if (!isControlPlane) return base;
  return [...(input.actAsRoles ?? [])];
}

/** True when these roles may write on the TENANT announcement surface. Derived
 *  from `ROLE_PERMISSIONS` so the UI cannot drift from the server matrix. */
export function canWriteTenantAnnouncements(
  roles: readonly ServiceRole[],
): boolean {
  return roles.some((role) =>
    (ROLE_PERMISSIONS[role] ?? []).includes('admin.announcements.write'),
  );
}

/** Hours before an expiry counts as "about to lapse" in the list. */
export const ANNOUNCEMENT_EXPIRY_WARNING_MS = 24 * 60 * 60 * 1000;

/**
 * Is this row's expiry close enough to warn about?
 *
 * An operator scanning the list cannot tell "expires tomorrow" from "expires
 * next quarter" by reading timestamps, and a PUBLISHED notice that lapses
 * unnoticed is indistinguishable from one that was never published.
 *
 * `status` is part of the question: a draft or an archived row is not visible
 * to anyone, so its expiry lapsing has no consequence to warn about.
 */
export function isAnnouncementExpiringSoon(
  expiresAt: string | null,
  nowMs: number,
  opts: { status?: AnnouncementStatus; withinMs?: number } = {},
): boolean {
  if (opts.status !== undefined && opts.status !== 'published') return false;
  if (!expiresAt) return false;
  const at = Date.parse(expiresAt);
  if (!Number.isFinite(at)) return false;
  // Already expired is not "expiring soon" — it is a different state, and the
  // row simply stopped being visible to members.
  return at > nowMs && at - nowMs <= (opts.withinMs ?? ANNOUNCEMENT_EXPIRY_WARNING_MS);
}

/** 0-based page → API offset. */
export function announcementOffset(page: number, pageSize: number): number {
  return Math.max(0, page) * pageSize;
}

/**
 * The page to show after a list response. Archiving the last row of the last
 * page leaves the viewer on a page that no longer exists; without this they see
 * an empty table and assume the data is gone.
 */
export function clampAnnouncementPage(
  page: number,
  total: number,
  pageSize: number,
): number {
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  return Math.min(Math.max(page, 0), lastPage);
}
